/**
 * Backgrounds Service
 *
 * GET /backgrounds — retrieves the background library from the Image Service API for the given
 * page and color flags.
 *   - doublePage (required, boolean): true for spread (double page) backgrounds, false for single
 *     page backgrounds.
 *   - fourColor (required, boolean): true for color backgrounds, false for black and white
 *     backgrounds.
 *   - backgroundType (optional, string): "image" or "solid color"; omitted is sent upstream as
 *     null.
 *
 * GET /backgrounds/keywords — retrieves the background keyword list from the Image Service API.
 * No parameters; fetched once, filtering against it happens entirely client-side (matches legacy
 * — see `filterBackgroundsByKeywords` in `lib/backgrounds/filter-backgrounds-by-keywords.ts`).
 */

import { httpClient } from '@/api/http/client'
import type {
  Background,
  BackgroundCmsKeyword,
  BackgroundKeywordsResponse,
  BackgroundsResponse,
} from '@/api/types/background'

export interface GetBackgroundsParams {
  doublePage: boolean
  fourColor: boolean
  backgroundType: string | null
}

function buildBackgroundsPath({
  doublePage,
  fourColor,
  backgroundType,
}: GetBackgroundsParams): string {
  const params = new URLSearchParams({
    doublePage: String(doublePage),
    fourColor: String(fourColor),
  })

  if (backgroundType != null) {
    params.set('backgroundType', backgroundType)
  }

  return `/backgrounds?${params.toString()}`
}

async function getBackgrounds(params: GetBackgroundsParams): Promise<Background[]> {
  const { result, payload } = await httpClient.get<BackgroundsResponse>(
    buildBackgroundsPath(params)
  )

  if (!result || !Array.isArray(payload)) {
    throw new Error('Failed to load backgrounds')
  }

  return payload
}

async function getBackgroundKeywords(): Promise<BackgroundCmsKeyword[]> {
  const { result, payload } =
    await httpClient.get<BackgroundKeywordsResponse>('/backgrounds/keywords')

  if (!result || !Array.isArray(payload)) {
    throw new Error('Failed to load background keywords')
  }

  return payload
}

export const backgroundsService = {
  getBackgrounds,
  getBackgroundKeywords,
}
